import constant from "./constant";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

export const sessionStorageUpdateEventName = 'iship:session-storage-updated';

const emitSessionStorageUpdated = (action, keyName = '') => {
  if (typeof window === 'undefined') return;

  window.dispatchEvent(new CustomEvent(sessionStorageUpdateEventName, {
    detail: {
      action,
      key: keyName,
      timestamp: Date.now(),
    },
  }));
};

export const removeStorage = (keyName) => {
  localStorage.removeItem(keyName);
}

export const setStorage = (key, jsonObj) => {
  localStorage.setItem(key, JSON.stringify(jsonObj))
}


export const getStorage = (keyName) => {
  const item = localStorage.getItem(keyName);
  return JSON.parse(item);
}

export const removeAllLocalStorage = () => {
  localStorage.clear();
}

export const removeSessionStorage = (keyName) => {
  sessionStorage.removeItem(keyName);
  emitSessionStorageUpdated('remove', keyName);
}

export const setSessionStorage = (key, jsonObj) => {
  sessionStorage.setItem(key, JSON.stringify(jsonObj));
  emitSessionStorageUpdated('set', key);
}

export const getSessionStorage = (keyName) => {
  const item = sessionStorage.getItem(keyName);
  return JSON.parse(item);
}

export const removeAllSessionStorage = () => {
  sessionStorage.clear();
  emitSessionStorageUpdated('clear');
}


export const validateFunctionalForm = (state, setState) => {
  const updatedState = { ...state };
  let status = true;
  Object.keys(updatedState).forEach(key => {
    const field = updatedState[key];

    if (field.isRequired) {
      const value = field?.value;

      if (value === undefined || value === null) {
        updatedState[key] = { ...field, error: 'This field is required' };
        status = false;
      } else if (typeof value === 'string' && value.trim() === '') {
        updatedState[key] = { ...field, error: 'This field is required' };
        status = false;
      } else if (Array.isArray(value) && value.length === 0) {
        updatedState[key] = { ...field, error: 'This field is required' };
        status = false;
      } else {
        updatedState[key] = { ...field, error: '' };
      }
    } else {
      updatedState[key] = { ...field, error: '' };
    }
  });

  setState(updatedState);
  return status;
};

export const REPORT_SUBJECT_PREFIXES = {
  fat: 'FAT:',
  inspection: 'Inspection:',
  ncr: 'NCR:',
};

const escapeRegExp = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const cleanEmailSubject = (value) => {
  if (value === undefined || value === null) return '';

  let normalizedValue = String(value).trim();
  const placeholderTokenPattern = /^(undefined|null|nan)$/i;

  if (placeholderTokenPattern.test(normalizedValue)) return '';

  let previousValue = '';
  while (previousValue !== normalizedValue) {
    previousValue = normalizedValue;
    normalizedValue = normalizedValue
      .replace(/\s*[\(\[\{]\s*(undefined|null|nan)\s*[\)\]\}]\s*$/i, '')
      .replace(/\s*(?:-|:|,)\s*(undefined|null|nan)\s*$/i, '')
      .replace(/\s+(undefined|null|nan)\s*$/i, '')
      .trim();
  }

  return placeholderTokenPattern.test(normalizedValue) ? '' : normalizedValue;
};

const sanitizeReportNamePart = (value) => {
  const normalizedValue = cleanEmailSubject(value);
  return normalizedValue
    .split('')
    .map((char) => (char.charCodeAt(0) < 32 || /[<>:"/\\|?*]/.test(char) ? ' ' : char))
    .join('')
    .replace(/\s+/g, ' ')
    .trim();
};

export const buildCommentReportName = (moduleName, referenceNumber, subjectText) => {
  const safeModuleName = sanitizeReportNamePart(moduleName);
  const safeReference = sanitizeReportNamePart(referenceNumber);
  const safeSubject = sanitizeReportNamePart(subjectText);
  const modulePrefix = safeModuleName ? `${safeModuleName} Comments` : 'Comments';

  return [modulePrefix, safeReference, safeSubject].filter(Boolean).join(' ');
};

export const buildReportAutoSubject = (subjectText, referenceNumber, prefix) => {
  const cleanSubject = cleanEmailSubject(subjectText);
  const cleanReference = cleanEmailSubject(referenceNumber);

  const cleanPrefix = typeof prefix === 'string' ? prefix.trim() : '';
  const prefixLabel = cleanPrefix.replace(/\s*:$/, '').trim();
  const existingPrefixPattern = prefixLabel
    ? new RegExp(`^\\s*${escapeRegExp(prefixLabel)}\\s*:`, 'i')
    : null;
  const subjectWithoutPrefix = existingPrefixPattern
    ? cleanSubject.replace(existingPrefixPattern, '').trim()
    : cleanSubject;

  let baseSubject = '';
  if (subjectWithoutPrefix && cleanReference) {
    const existingReferencePattern = new RegExp(`\\(${escapeRegExp(cleanReference)}\\)\\s*$`, 'i');
    baseSubject = existingReferencePattern.test(subjectWithoutPrefix)
      ? subjectWithoutPrefix
      : `${subjectWithoutPrefix} (${cleanReference})`;
  } else if (subjectWithoutPrefix) baseSubject = subjectWithoutPrefix;
  else if (cleanReference) baseSubject = cleanReference;

  if (!prefixLabel) return baseSubject;

  const normalizedPrefix = `${prefixLabel}:`;

  return baseSubject ? `${normalizedPrefix} ${baseSubject}` : normalizedPrefix;
};

export const getAuthHeader = () => {
  // const token = getStorage(constant.keys.userToken);
  const token = getSessionStorage(constant.keys.userToken);
  const header = {
    headers: { Authorization: "Bearer " + token }
  };
  return header;
}

export const getLoggedInUserRoleId = () => {
  try {
    const token = getSessionStorage(constant.keys.userToken);
    const payload = token?.split('.')?.[1];
    if (!payload) return null;

    const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
    const paddedPayload = normalizedPayload.padEnd(
      normalizedPayload.length + ((4 - (normalizedPayload.length % 4)) % 4),
      '='
    );
    const binaryPayload = atob(paddedPayload);
    const payloadText = typeof TextDecoder === 'undefined'
      ? binaryPayload
      : new TextDecoder('utf-8').decode(Uint8Array.from(binaryPayload, (char) => char.charCodeAt(0)));
    const decodedPayload = JSON.parse(payloadText);
    return decodedPayload?.ROLE_ID || null;
  } catch (error) {
    console.error('Failed to get user role:', error);
    return null;
  }
};

export const getLoggedInUserId = () => {
  try {
    const token = getSessionStorage(constant.keys.userToken);
    const payload = token?.split('.')?.[1];
    if (!payload) return null;

    const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
    const paddedPayload = normalizedPayload.padEnd(
      normalizedPayload.length + ((4 - (normalizedPayload.length % 4)) % 4),
      '='
    );
    const binaryPayload = atob(paddedPayload);
    const payloadText = typeof TextDecoder === 'undefined'
      ? binaryPayload
      : new TextDecoder('utf-8').decode(Uint8Array.from(binaryPayload, (char) => char.charCodeAt(0)));
    const decodedPayload = JSON.parse(payloadText);
    return decodedPayload?.USER_ID || null;
  } catch (error) {
    console.error('Failed to get user ID:', error);
    return null;
  }
};

export const getLoggedInUserGender = () => {
  try {
    const user = JSON.parse(atob(getSessionStorage(constant.keys.userDetails)));
    return user?.gender || null;
  } catch (error) {
    console.error('Failed to get user gender:', error);
    return null;
  }
};



export const exportTableToExcel = (tableId, fileName = 'Export.xlsx') => {
  const table = document.getElementById(tableId);
  const workbook = XLSX.utils.table_to_book(table, { sheet: 'Sheet1' });
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
  saveAs(data, fileName);
};

export const formatRowsForExcel = (rows = [], columns = []) => (
  rows.map((row, index) => columns.reduce((acc, column) => {
    const value = typeof column.value === 'function'
      ? column.value(row, index)
      : row?.[column.key];

    acc[column.label] = value ?? '';
    return acc;
  }, {}))
);

export const exportRowsToExcel = ({ rows = [], columns = [], sheetName = 'Sheet1', fileName = 'Export.xlsx' }) => {
  const selectedColumns = columns.filter((column) => column && column.label);
  const formattedRows = formatRowsForExcel(rows, selectedColumns);
  const worksheet = XLSX.utils.json_to_sheet(formattedRows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
  saveAs(data, fileName);
};

const sanitizeFileNameToken = (value) => (
  String(value || '')
    .trim()
    .replace(/[^a-zA-Z0-9_-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
);

const formatHumanDateFromParts = (year, month, day) => {
  if (!year || !month || !day) return '';
  if (month < 1 || month > 12) return '';
  if (day < 1 || day > 31) return '';

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${String(day).padStart(2, '0')}-${monthNames[month - 1]}-${year}`;
};

const normalizeFilterDateToken = (value) => {
  const normalized = String(value || '').trim();
  if (!normalized) return '';

  const isoDateMatch = normalized.match(/^(\d{4})[-/](\d{2})[-/](\d{2})/);
  if (isoDateMatch) {
    const year = Number(isoDateMatch[1]);
    const month = Number(isoDateMatch[2]);
    const day = Number(isoDateMatch[3]);
    return formatHumanDateFromParts(year, month, day);
  }

  const ddmmyyyyMatch = normalized.match(/^(\d{2})[-/](\d{2})[-/](\d{4})/);
  if (ddmmyyyyMatch) {
    const day = Number(ddmmyyyyMatch[1]);
    const month = Number(ddmmyyyyMatch[2]);
    const year = Number(ddmmyyyyMatch[3]);
    return formatHumanDateFromParts(year, month, day);
  }

  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return '';

  return formatHumanDateFromParts(
    parsed.getFullYear(),
    parsed.getMonth() + 1,
    parsed.getDate()
  );
};

const pickDateFromFilter = (filterModel, keys) => {
  for (const key of keys) {
    const rawValue = filterModel?.[key];
    const value = rawValue && typeof rawValue === 'object' && 'value' in rawValue
      ? rawValue.value
      : rawValue;

    const token = normalizeFilterDateToken(value);
    if (token) return token;
  }

  return '';
};

export const getDateRangeLabelFromFilter = (filterModel = {}) => {
  const fromDate = pickDateFromFilter(filterModel, ['startDate', 'fromDate', 'dateFrom', 'from']);
  const toDate = pickDateFromFilter(filterModel, ['endDate', 'toDate', 'dateTo', 'to']);

  if (!fromDate && !toDate) return '';
  if (fromDate && toDate) {
    return fromDate === toDate ? fromDate : `${fromDate}_to_${toDate}`;
  }
  return fromDate ? `from_${fromDate}` : `to_${toDate}`;
};

export const appendDateRangeToFileName = (fileName, filterModel = {}) => {
  const baseName = String(fileName || '').trim();
  if (!baseName) return baseName;

  const dateRangeLabel = sanitizeFileNameToken(getDateRangeLabelFromFilter(filterModel));
  if (!dateRangeLabel || baseName.includes(dateRangeLabel)) {
    return baseName;
  }

  return `${baseName}_${dateRangeLabel}`;
};

export const decodeBase64JsonPayload = (encodedPayload = '') => {
  const normalizedPayload = String(encodedPayload || '').trim();
  if (!normalizedPayload) return {};

  const tryParse = (value) => {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
      return {};
    }
  };

  try {
    const binary = atob(normalizedPayload);
    if (typeof TextDecoder !== 'undefined') {
      const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
      return tryParse(new TextDecoder().decode(bytes));
    }

    return tryParse(decodeURIComponent(escape(binary)));
  } catch (error) {
    try {
      return tryParse(atob(normalizedPayload));
    } catch (fallbackError) {
      return {};
    }
  }
};

export const encodeBase64JsonPayload = (payload = {}) => {
  const jsonPayload = JSON.stringify(payload ?? {});

  try {
    if (typeof TextEncoder !== 'undefined') {
      const bytes = new TextEncoder().encode(jsonPayload);
      let binary = '';
      const chunkSize = 0x8000;

      for (let index = 0; index < bytes.length; index += chunkSize) {
        binary += String.fromCharCode(...bytes.subarray(index, index + chunkSize));
      }

      return btoa(binary);
    }

    return btoa(unescape(encodeURIComponent(jsonPayload)));
  } catch (error) {
    return '';
  }
};

// Base64 that is safe to drop into a query string unencoded: '+' otherwise
// arrives at the server as a space and the filter silently decodes to {}.
export const encodeBase64UrlJsonPayload = (payload = {}) =>
  encodeBase64JsonPayload(payload).replace(/\+/g, '-').replace(/\//g, '_');

export const playNotificationSound = () => {
  const audio = new Audio('/sounds/notification.mp3');
  audio.play().catch(err => {
    console.warn('Audio play failed:', err);
  });
};

export const getNotificationOriginName = (notification = {}) => {
  const meta = notification?.meta || {};
  const candidates = [
    notification?.originName,
    meta?.originName,
    meta?.fromName,
    meta?.assignedByName,
    meta?.commentedByName,
    meta?.sender,
    meta?.emailFromName,
    meta?.mailboxEmail,
  ];

  for (const candidate of candidates) {
    const normalized = String(candidate || '').trim();
    if (normalized) {
      return normalized;
    }
  }

  return '';
};

/**
 * Persist list state (page number, filters) into the current history entry.
 *
 * List pages call this from inside the `.then()` of their data fetch. If the
 * user navigates away while that request is still in flight, the late response
 * would otherwise `history.replace()` the browser back to the page they just
 * left. Two list pages doing this bounce the URL between each other
 * indefinitely, and neither ever renders. Bail out when we are no longer on the
 * route the state belongs to.
 */
export const replacePageState = (history, url, state) => {
  if (!history || history.location?.pathname !== url) return;
  history.replace(url, state);
};
